import * as path from 'node:path';
import { createHash } from 'node:crypto';
import type { ActiveAbility, ActiveSkill, MaterializedAbility } from './types.js';
import { buildSkillMd, buildAgentMd } from './synthesize.js';
import { splitFrontmatterBlock } from './frontmatter.js';
import type { AbilityFsWriter, AbilitySweepWriter } from './writer.js';
import { digestAbilityLines } from './head.js';
import { buildPartsIndex, partKeyOf, partFileMap, type PartRef } from './parts.js';
import { buildManifestV2, computePressingHash, stableStringify } from './manifest-v2.js';

/** Ability Format v2 opt-in. Absent/false = today's on-disk shape, byte-identical. */
/**
 * Per-home options that change the checksum formula. Threaded from each home's ONE
 * helper (server util/ability-format-flag.ts, sandbox abilities/format-flag.ts
 * `abilityFormatOpts()`), never read from process.env here, so the server's version,
 * the server-side materialize markers, the box's converge markers and its drive head
 * all hash under the same formula.
 *
 * `hookFingerprint` (HOOK_FINGERPRINT_V1): each skill contributes its whole SERVED
 * entry instead of a hand-picked field list — see {@link computeChecksum}.
 */
export interface FormatOpts { formatV2?: boolean; hookFingerprint?: boolean }

/** Injected per-environment storage-asset materializers. `unpackArchive` unpacks
 *  a `kind:'archive'` bundle into the skill dir; `writeAsset` writes a single-file
 *  `kind:'asset'` to its target_path (server = supabase download; sandbox =
 *  server-proxy fetch). An absent callback → that asset is skipped and the skill
 *  degrades to SKILL.md + inline files (the marker is poisoned so a later pass
 *  retries). */
export interface ArchiveUnpackOpts {
  unpackArchive?: (key: string, destSkillDirAbsPath: string) => Promise<boolean>;
  writeAsset?: (key: string, destAbsPath: string) => Promise<boolean>;
}

/**
 * Materialize ONE skill's storage_assets into its on-disk dir. Archive entries
 * unpack via `opts.unpackArchive`; single-file `kind:'asset'` entries write their
 * bytes to `target_path` via `opts.writeAsset` (spec §3). Materialized files
 * bypass the string-only `write()` helper (binary safety), so they are NOT in the
 * marker `files` map — at-rest tamper verification of the asset tree is a noted
 * follow-on; the asset keys are folded into computeChecksum so a change
 * re-materializes. Returns false when ANY declared asset could not be
 * materialized (no injected callback, or the callback degraded) so the caller
 * poisons the marker and forces a retry — never a silent success. Returns true
 * when there is nothing to do or every asset materialized. Shared by BOTH
 * materializeAbility (full) and materializeAbilityParts (incremental).
 */
async function materializeSkillAssets(
  skill: ActiveSkill,
  destSkillDir: string,
  opts?: ArchiveUnpackOpts,
): Promise<boolean> {
  let ok = true;
  for (const asset of skill.storage_assets ?? []) {
    if (asset.kind === 'archive') {
      if (opts?.unpackArchive) { if (!(await opts.unpackArchive(asset.key, destSkillDir))) ok = false; }
      else ok = false; // no unpacker injected → degrade
    } else {
      const dest = asset.target_path ? path.posix.join(destSkillDir, asset.target_path) : destSkillDir;
      if (opts?.writeAsset) { if (!(await opts.writeAsset(asset.key, dest))) ok = false; }
      else ok = false; // no asset writer injected → degrade
    }
  }
  return ok;
}

export interface InstalledMarker {
  slug: string;
  version: string;
  checksum: string;
  materializedAt: string;
  /**
   * Marker v2 content map (additive/optional — NO MATERIALIZER_VERSION bump).
   * `<bundle-relative posix path>` → sha256(written content) for every file the
   * materializer wrote EXCEPT `.installed` itself. Powers at-rest tamper
   * detection ({@link verifyDiscOnDisk}). Markers written before v2 lack this
   * field; they stay valid and verify as `'no-map'` until their next
   * materialize upgrades them.
   */
  files?: Record<string, string>;
  /**
   * Marker v3 (ABILITY_FORMAT_V2 only, additive): the pressing this disc
   * materializes and the per-part content map (`<kind>:<slug>` → part hash).
   * Powers converge-by-part diffing. Absent on v2 markers — converge falls
   * back to whole-disc repair for those.
   */
  pressingHash?: string;
  parts?: Record<string, string>;
}

/**
 * Materializer layout version. Bump whenever the on-disk shape changes so
 * existing `.installed` markers are invalidated and abilities re-materialize
 * with the new shape. v3 (Forge): the bundle lives at `.forge/abilities/<slug>`
 * and carries `ability.json` (our rich manifest). The 3-field Claude plugin.json
 * moved OUT of the bundle into the emitted `.claude/forge-plugins/<slug>/` view;
 * standalone (0-ability) items stay at `.claude/{skills,agents}`. Layout `v4` is
 * RESERVED by the formatV2 path (computeChecksum stamps `layout:v4` under the
 * flag), so never bump this constant to 4 — the materialize-v4 checksum test
 * fails loudly on the collision.
 */
const MATERIALIZER_VERSION = 3;

/**
 * The per-ability "voice" / operating-notes fragment, written into the bundle.
 * Vendor-neutral name (NEVER `CLAUDE.md`) so `.forge/abilities/<slug>/` carries
 * no Anthropic-branded filenames. A legacy `CLAUDE.md` fragment is mapped here
 * on write (see `parts.ts`).
 */
export const ABILITY_GUIDE_FILENAME = 'guide.md';

/** Coerce an ability's `manifest` jsonb to a safe record (null/array/non-object → {}). */
function asManifestRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

/**
 * A slug the materializer may join onto the abilities root: one plain folder name, nothing a path
 * could climb out with. The write below starts with an rm -rf of `<root>/<slug>`, so a slug like
 * `../..` (a row renamed through the database, say) would delete whatever it names.
 */
export function isPlainFolderName(slug: string): boolean {
  return slug.length > 0 && slug.length <= 128 && slug !== '.' && slug !== '..' && !/[\\/\0]/.test(slug);
}

export async function materializeAbility(
  active: ActiveAbility,
  root: string,
  writer: AbilityFsWriter,
  opts?: { force?: boolean } & FormatOpts & ArchiveUnpackOpts,
): Promise<MaterializedAbility> {
  if (!isPlainFolderName(active.ability.slug)) {
    throw new Error(`refusing to materialize ability ${active.ability.id}: its slug is not a plain folder name`);
  }
  const dirPath = path.posix.join(root, active.ability.slug);
  const checksum = computeChecksum(active, opts);
  const markerPath = path.posix.join(dirPath, '.installed');

  const existing = await writer.readFile(markerPath);
  // `force` skips the marker short-circuit so a caller (P2 repair loop) can
  // rebuild a disc whose content drifted even though its checksum still matches.
  if (!opts?.force && existing) {
    try {
      const marker = JSON.parse(existing) as InstalledMarker;
      if (marker.checksum === checksum) return { slug: active.ability.slug, dirPath, checksum };
    } catch { /* fall through and rebuild */ }
  }

  await writer.rm(dirPath, true);
  await writer.mkdir(dirPath, true);

  // Every bundle file is written through this helper so the marker's content
  // map (`files`) cannot drift from what was actually written: it records the
  // sha256 of each bundle-relative path as a side effect of the write.
  // `.installed` is the one file written OUTSIDE this helper — it carries the
  // map, so it can't hash itself.
  const files: Record<string, string> = {};
  const write = async (rel: string, content: string): Promise<void> => {
    const filePath = path.posix.join(dirPath, rel);
    await writer.mkdir(path.posix.dirname(filePath), true);
    await writer.writeFile(filePath, content);
    files[rel] = createHash('sha256').update(content).digest('hex');
  };

  // 1) ability-owned files. The per-ability voice fragment is canonicalized to
  //    guide.md on disk (a legacy CLAUDE.md path is mapped) so the bundle stays
  //    vendor-clean.
  for (const f of active.files) {
    if (f.is_binary) continue;
    if (f.content === null) continue;
    const diskRel = f.path === 'CLAUDE.md' ? ABILITY_GUIDE_FILENAME : f.path;
    await write(diskRel, f.content);
  }

  // 2) skills (synthesized SKILL.md + reference files). The stored content is
  //    split first so a legacy row carrying inline frontmatter never stacks a
  //    second block under the synthesized one — exactly one frontmatter block
  //    on disk, emitted from row metadata, for every row shape.
  // Set true when a declared storage asset could not be materialized (degraded).
  // A degraded pass must NOT stamp a clean success marker (see marker poison below).
  let archiveDegraded = false;
  for (const s of active.skills) {
    const body = splitFrontmatterBlock(s.content ?? '').body;
    await write(path.posix.join('skills', s.slug, 'SKILL.md'), buildSkillMd({ ...s, content: body }));
    for (const f of s.files) {
      if (f.path === 'SKILL.md') continue;
      if (f.content === null) continue;
      await write(path.posix.join('skills', s.slug, f.path), f.content);
    }
    // Bundled app archive + single-file assets: materialize into the skill dir
    // (archive unpacks at the root; a single file writes to its target_path).
    if ((s.storage_assets ?? []).length > 0) {
      const destSkillDir = path.posix.join(dirPath, 'skills', s.slug);
      if (!(await materializeSkillAssets(s, destSkillDir, opts))) archiveDegraded = true;
    }
  }

  // 3) agents (synthesized agent.md — same single-frontmatter invariant)
  for (const a of active.agents) {
    const prompt = splitFrontmatterBlock(a.prompt ?? '').body;
    await write(path.posix.join('agents', `${a.slug}.md`), buildAgentMd({ ...a, prompt }));
  }

  // 3b) mcps — durable, declared config (secret refs intact) for UI/audit/
  //     portability. NOT the runtime source of truth: the runtime path fetches
  //     resolved configs separately so secrets stay resolved server-side.
  for (const m of active.mcps ?? []) {
    await write(
      path.posix.join('mcps', m.slug, 'mcp.json'),
      JSON.stringify({ slug: m.slug, connection_config: m.connection_config ?? {} }, null, 2),
    );
  }

  // 4) ability.json — OUR rich manifest (serialized AbilityManifest + scope +
  //    a contents index). The 3-field Claude plugin.json is NOT written into
  //    the bundle anymore; it lives only in the emitted
  //    .claude/forge-plugins/<slug>/.claude-plugin/plugin.json view
  //    (emitClaudePluginView, which still calls buildPluginManifest).
  await write('ability.json', JSON.stringify(buildAbilityJson(active, opts), null, 2));

  const marker: InstalledMarker = {
    slug: active.ability.slug,
    version: active.ability.version,
    // A degraded asset materialize stores a sentinel that can never equal the
    // clean checksum recomputed on the next materialize, so the short-circuit
    // is skipped and the fetch is retried (spec: "degraded, not corrupt; a
    // later materialize retries"). A clean pass stores `checksum`.
    checksum: archiveDegraded ? `degraded:${checksum}` : checksum,
    materializedAt: new Date().toISOString(),
    files,
    ...(opts?.formatV2
      ? {
          pressingHash: computePressingHash(buildManifestV2(active)),
          parts: Object.fromEntries(buildPartsIndex(active).map((p) => [partKeyOf(p), p.hash])),
        }
      : {}),
  };
  await writer.writeFile(markerPath, JSON.stringify(marker, null, 2));
  return { slug: active.ability.slug, dirPath, checksum };
}

/**
 * Surgical part-level repair (P4a): rewrite only the changed skill/agent
 * parts, prune removed ones, refresh ability.json + a v3 marker. The caller
 * (converge) guarantees the plan is not wholeDisc and the disc is not
 * tampered. Marker files map = old map minus touched-part paths plus new writes.
 */
export async function materializeAbilityParts(
  active: ActiveAbility,
  root: string,
  writer: AbilityFsWriter,
  plan: { changed: PartRef[]; removedKeys: string[] },
  oldMarker: InstalledMarker,
  opts?: ArchiveUnpackOpts & FormatOpts,
): Promise<MaterializedAbility> {
  const dirPath = path.posix.join(root, active.ability.slug);
  const files: Record<string, string> = { ...(oldMarker.files ?? {}) };

  const dropPrefix = (prefix: string) => {
    for (const k of Object.keys(files)) if (k === prefix || k.startsWith(`${prefix}/`)) delete files[k];
  };
  const partRoot = (key: string): string =>
    key.startsWith('skill:') ? path.posix.join('skills', key.slice('skill:'.length))
    : path.posix.join('agents', `${key.slice('agent:'.length)}.md`);

  for (const key of plan.removedKeys) {
    dropPrefix(partRoot(key));
    await writer.rm(path.posix.join(dirPath, partRoot(key)), key.startsWith('skill:'));
  }
  let archiveDegraded = false;
  for (const p of plan.changed) {
    const key = partKeyOf(p);
    dropPrefix(partRoot(key));
    if (p.kind === 'skill') await writer.rm(path.posix.join(dirPath, partRoot(key)), true);
    const map = partFileMap(active, p);
    if (!map) throw new Error(`part vanished mid-repair: ${key}`);
    for (const [rel, content] of Object.entries(map)) {
      const filePath = path.posix.join(dirPath, rel);
      await writer.mkdir(path.posix.dirname(filePath), true);
      await writer.writeFile(filePath, content);
      files[rel] = createHash('sha256').update(content).digest('hex');
    }
    // The `writer.rm` above WIPED skills/<slug>, including the archive-unpacked
    // engine tree (which is not in `partFileMap` — that map is inline files only).
    // Re-materialize the changed skill's assets so the tree survives the repair;
    // otherwise a clean marker would stamp success at the archive-inclusive
    // checksum with only SKILL.md on disk. Same degrade-not-corrupt contract.
    if (p.kind === 'skill') {
      const skill = active.skills.find((s) => s.slug === p.slug);
      if (skill && !(await materializeSkillAssets(skill, path.posix.join(dirPath, 'skills', skill.slug), opts))) {
        archiveDegraded = true;
      }
    }
  }

  const abilityJson = JSON.stringify(buildAbilityJson(active, { formatV2: true }), null, 2);
  const ajPath = path.posix.join(dirPath, 'ability.json');
  await writer.writeFile(ajPath, abilityJson);
  files['ability.json'] = createHash('sha256').update(abilityJson).digest('hex');

  // The part path runs only under v2; every other formula option rides through, so
  // this marker matches the probe's activeChecksum (a mismatch re-repairs forever).
  const checksum = computeChecksum(active, { formatV2: true, ...(opts?.hookFingerprint ? { hookFingerprint: true } : {}) });
  const marker: InstalledMarker = {
    slug: active.ability.slug,
    version: active.ability.version,
    // Poison on degrade so a later converge cannot short-circuit on this marker
    // and re-fetches the asset (same contract as the full path). The RETURN keeps
    // the clean checksum — the sentinel lives only on disk.
    checksum: archiveDegraded ? `degraded:${checksum}` : checksum,
    materializedAt: new Date().toISOString(),
    files,
    pressingHash: computePressingHash(buildManifestV2(active)),
    parts: Object.fromEntries(buildPartsIndex(active).map((x) => [partKeyOf(x), x.hash])),
  };
  await writer.writeFile(path.posix.join(dirPath, '.installed'), JSON.stringify(marker, null, 2));
  return { slug: active.ability.slug, dirPath, checksum };
}

export function buildPluginManifest(active: ActiveAbility): {
  name: string;
  version: string;
  description: string;
} {
  const ability = active.ability;
  // asManifestRecord projects the jsonb manifest to a safe record for the
  // defensive fallbacks (handles null / array / non-object).
  const manifest = asManifestRecord(ability.manifest);
  return {
    name: ability.slug,
    version: String(ability.version ?? manifest.version ?? '1.0.0'),
    description:
      (ability.description as string | null | undefined) ??
      (manifest.description as string | undefined) ??
      (ability.name as string | undefined) ??
      '',
  };
}

/**
 * Build `ability.json` — OUR rich, vendor-neutral manifest written into the
 * `.forge/abilities/<slug>/` bundle. It is the serialized `AbilityManifest`
 * (the manifest jsonb) with authoritative Tier-1 fields taken from the ability
 * row (the jsonb can be sparse), plus `scope` and a `contents` index. Unlike
 * the `.strict()` Claude plugin.json, it can hold the full metadata surface.
 */
export function buildAbilityJson(active: ActiveAbility, opts?: FormatOpts): Record<string, unknown> {
  const ability = active.ability;
  const base: Record<string, unknown> = {
    // Spread the manifest FIRST, then let authoritative fields WIN over any
    // stale / sparse / self-referential copy (e.g. a manifest carrying its own
    // `schema` or a drifted `slug`).
    ...asManifestRecord(ability.manifest),
    schema: 'vestra.ability/v1',
    name: ability.name,
    slug: ability.slug,
    version: ability.version,
    description: ability.description,
    scope: ability.scope,
    contents: {
      skills: active.skills.map((s) => s.slug),
      agents: active.agents.map((a) => a.slug),
      mcps: (active.mcps ?? []).map((m) => m.slug),
    },
  };
  if (opts?.formatV2) {
    base.schema = 'vestra.ability/v2';
    base.parts = buildPartsIndex(active);
  }
  return base;
}

/**
 * Optional fleet-wide cleanup of pre-abilities-cutover canonical paths.
 *
 * Before the abilities model existed, system agents and skills lived at the
 * workspace root: `<workspace>/.claude/agents/<slug>.md` and
 * `<workspace>/.claude/skills/<slug>/`. After the cutover (2026-05) every
 * agent and skill is materialized under its owning ability and the root
 * paths SHOULD be empty — but pre-cutover volumes carry the stale flat
 * files indefinitely because nothing sweeps them.
 *
 * Passing this options bag to {@link sweepOrphanAbilities} also walks those
 * root paths and removes any entry whose basename matches a currently-active
 * agent or skill slug (i.e. the same name now lives canonically under an
 * ability dir). Entries whose names DON'T match an active slug are left
 * alone so user-authored flat files survive.
 */
export interface LegacyRootSweepOptions {
  /** Path that contains the `agents/` and `skills/` subdirs. Usually `<workspace>/.claude`. */
  workspaceRoot: string;
  /** Slugs of agents currently bundled in any active ability. */
  activeAgentSlugs: Iterable<string>;
  /** Slugs of skills currently bundled in any active ability. */
  activeSkillSlugs: Iterable<string>;
}

export async function sweepOrphanAbilities(
  abilitiesRoot: string,
  activeSlugs: Iterable<string>,
  writer: AbilitySweepWriter,
  legacy?: LegacyRootSweepOptions,
): Promise<string[]> {
  const active = new Set(activeSlugs);
  let entries: string[];
  try { entries = await writer.readdir(abilitiesRoot); }
  catch { return []; }
  const removed: string[] = [];
  for (const name of entries) {
    if (active.has(name) || name.startsWith('.')) continue;
    const fullPath = path.posix.join(abilitiesRoot, name);
    try {
      if (!(await writer.isDirectory(fullPath))) continue;
      await writer.rm(fullPath, true);
      removed.push(name);
    } catch { /* tolerate single-folder failure */ }
  }

  if (legacy) {
    const legacyRemoved = await sweepLegacyCanonicalPaths(legacy, writer);
    removed.push(...legacyRemoved);
  }

  return removed;
}

/**
 * Sweep pre-cutover `<workspaceRoot>/agents/<slug>.md` files and
 * `<workspaceRoot>/skills/<slug>/` dirs whose basenames match a currently-
 * active item. Anything that doesn't match an active slug is preserved as
 * potential user-authored content. Safe to call repeatedly; idempotent.
 *
 * Returned slugs are prefixed with `legacy-agents/` or `legacy-skills/` so
 * callers can distinguish them from regular orphan ability slugs in logs.
 */
async function sweepLegacyCanonicalPaths(
  opts: LegacyRootSweepOptions,
  writer: AbilitySweepWriter,
): Promise<string[]> {
  const removed: string[] = [];
  const activeAgents = new Set(opts.activeAgentSlugs);
  const activeSkills = new Set(opts.activeSkillSlugs);

  // Agents live as flat files: <root>/agents/<slug>.md
  const agentsDir = path.posix.join(opts.workspaceRoot, 'agents');
  let agentEntries: string[] = [];
  try { agentEntries = await writer.readdir(agentsDir); } catch { /* dir doesn't exist; nothing to do */ }
  for (const entry of agentEntries) {
    if (!entry.endsWith('.md')) continue;
    const slug = entry.slice(0, -3);
    if (!activeAgents.has(slug)) continue;
    const fullPath = path.posix.join(agentsDir, entry);
    try {
      await writer.rm(fullPath, false);
      removed.push(`legacy-agents/${slug}`);
    } catch { /* tolerate single-file failure */ }
  }

  // Skills live as directories: <root>/skills/<slug>/
  const skillsDir = path.posix.join(opts.workspaceRoot, 'skills');
  let skillEntries: string[] = [];
  try { skillEntries = await writer.readdir(skillsDir); } catch { /* dir doesn't exist; nothing to do */ }
  for (const entry of skillEntries) {
    if (entry.startsWith('.')) continue;
    if (!activeSkills.has(entry)) continue;
    const fullPath = path.posix.join(skillsDir, entry);
    try {
      if (!(await writer.isDirectory(fullPath))) continue;
      await writer.rm(fullPath, true);
      removed.push(`legacy-skills/${entry}`);
    } catch { /* tolerate single-folder failure */ }
  }

  return removed;
}

export function computeChecksum(active: ActiveAbility, opts?: FormatOpts): string {
  const h = createHash('sha256');
  // Layout-version goes first so a bump to MATERIALIZER_VERSION invalidates
  // every existing .installed marker across the fleet (forces re-materialize
  // with the new on-disk shape on the next install / sandbox wake). Under
  // formatV2 the line reads `layout:v4`, so flipping the flag re-materializes
  // the whole fleet onto the v2 shape (and back off it).
  h.update(`layout:v${opts?.formatV2 ? 4 : MATERIALIZER_VERSION}`);
  h.update(active.ability.id);
  h.update(active.ability.version);
  const sortedFiles = [...active.files].sort((a, b) => a.path.localeCompare(b.path));
  for (const f of sortedFiles) { h.update(`file:${f.path}`); h.update(f.checksum ?? ''); }
  const sortedSkills = [...active.skills].sort((a, b) => a.slug.localeCompare(b.slug));
  for (const s of sortedSkills) {
    if (opts?.hookFingerprint) {
      // HOOK_FINGERPRINT_V1 — the SERVED bytes, not a hand list. The list below was
      // written before skills carried hooks (and tools, mcps, category, color,
      // allowed_tools, args_schema), so a hook edit never moved the version, the head
      // or the marker, and no box ever re-synced it. Hashing the whole entry the
      // loader minted (load.ts mintActiveSkill) covers every served field by
      // construction. A skill entry carries no volatile value (see the mint); the
      // ability row's volatile columns stay out — it still contributes id + version.
      h.update(`skill:${stableStringify(servedSkillEntry(s))}`);
      continue;
    }
    h.update(`skill:${s.slug}`); h.update(s.content ?? ''); h.update(s.icon ?? ''); h.update(s.description ?? '');
    const sf = [...s.files].sort((a, b) => a.path.localeCompare(b.path));
    for (const f of sf) { h.update(`skillfile:${f.path}`); h.update(f.content ?? ''); }
    // Storage-backed assets (archive/CAS + single-file). Absent → zero iterations → byte-identical.
    const sa = [...(s.storage_assets ?? [])].sort((a, b) => a.key.localeCompare(b.key));
    for (const a of sa) { h.update(`skillasset:${a.target_path}`); h.update(a.key); }
  }
  const sortedAgents = [...active.agents].sort((a, b) => a.slug.localeCompare(b.slug));
  for (const a of sortedAgents) {
    h.update(`agent:${a.slug}`); h.update(a.prompt ?? ''); h.update(a.avatar_url ?? ''); h.update(a.color ?? ''); h.update(a.description ?? '');
  }
  const sortedMcps = [...(active.mcps ?? [])].sort((a, b) => a.slug.localeCompare(b.slug));
  for (const m of sortedMcps) {
    h.update(`mcp:${m.slug}`);
    h.update(JSON.stringify(m.connection_config ?? {}));
  }
  return h.digest('hex');
}

/**
 * A skill entry in canonical form for hashing: files sorted by path and assets by key
 * (row order is not identity — the legacy lines sort them too). `stableStringify` sorts
 * keys and drops `undefined` exactly as the JSON wire does, so the server's in-memory
 * entry and the box's parsed payload (or snapshot) hash to the same bytes.
 */
function servedSkillEntry(s: ActiveSkill): ActiveSkill {
  return {
    ...s,
    files: [...s.files].sort((a, b) => a.path.localeCompare(b.path)),
    ...(s.storage_assets ? { storage_assets: [...s.storage_assets].sort((a, b) => a.key.localeCompare(b.key)) } : {}),
  };
}

/**
 * One stable digest over a user's entire active-ability set — the cache key the
 * sandbox version-gate sends to the server as `?since=` to decide whether the
 * full payload needs re-downloading + re-materializing on a new task's first turn.
 *
 * Built as a digest-of-digests: each ability contributes `<slug>:<computeChecksum>`,
 * so the value flips on ANY content change (computeChecksum already covers layout
 * version, ability id/version, files, skills + skill-files, agents) AND on any
 * add/remove (the set of lines changes). Sorting by the `slug:checksum` line makes
 * it order-independent — the server and the sandbox derive identical versions
 * regardless of row ordering. The empty set yields a fixed constant (sha256 of '').
 */
export function computeActiveAbilitiesVersion(abilities: ActiveAbility[], opts?: FormatOpts): string {
  return digestAbilityLines(abilities.map((a) => `${a.ability.slug}:${computeChecksum(a, opts)}`));
}
